import { useState } from 'react'

import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  Link,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { Modal } from '~/components/common/Modal'
import { convertAreaLabel } from '~/models/area'
import { isIntradayMarketOrder } from '~/models/intradayOrderMethod'
import {
  type IntradaySubmitFailure,
  intradaySubmitFailureReason,
} from '~/pages/IntradayBidding/models/intradaySubmitFailure'

type Props = {
  /** 通った件数 (承認申請・予約も数える)。 0 件の時は見出しを出さず本文だけにする。 */
  passedCount: number
  /** 通らなかった札。 入札制限違反・自動取消時刻の超過で外した札は含めない。 */
  failures: readonly IntradaySubmitFailure[]
  onDismiss: () => void
}

const LICENSE_TYPE_OF_ISSUER = {
  generation: LicenseType.GENERATION,
  retail: LicenseType.RETAIL,
  selfwheeling: LicenseType.SELF_WHEELING,
} as const

/**
 * 時間前の提出の一部が通らなかった時、 または 1 件も通らず、 画面の更新・作り直しが要る失敗が
 * 1 件でもある時のエラーバナー (AC-BIN-009。 入札全削除の一部失敗〈AC-BIN-138〉と同じ形)。
 * 見出しは通った件数 (0 件の時は出さず本文だけ)、 本文は照合で通らなかった件数 {M} とそれ以外の件数 {K}。
 * 札ごとの理由はバナーに並べず、 「詳細を見る」 のダイアログに出す。
 */
export const IntradaySubmitFailureBanner = ({
  passedCount,
  failures,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [detailOpen, setDetailOpen] = useState(false)
  const conflictCount = failures.filter((f) => f.kind === 'conflict').length
  const otherCount = failures.length - conflictCount

  const issuerLabelOf = (f: IntradaySubmitFailure): string => {
    const licenseType =
      f.bid.issuerType === 'generation' ||
      f.bid.issuerType === 'retail' ||
      f.bid.issuerType === 'selfwheeling'
        ? LICENSE_TYPE_OF_ISSUER[f.bid.issuerType]
        : undefined
    return licenseType === undefined ? '—' : issuerTypeLabel(licenseType)
  }
  const columns: Array<MasterDataTableColumn<IntradaySubmitFailure>> = [
    {
      key: 'timeCode',
      header: t('page:biddingIntraday.column.timeCode'),
      cell: (f) => f.bid.timeCode,
    },
    {
      key: 'area',
      header: t('page:biddingIntraday.column.area'),
      cell: (f) => convertAreaLabel(f.bid.areaCode),
    },
    {
      key: 'issuerType',
      header: t('page:biddingIntraday.submit.failure.columnIssuerType'),
      cell: issuerLabelOf,
    },
    {
      key: 'side',
      header: t('page:biddingIntraday.column.side'),
      cell: (f) =>
        f.bid.direction === Side.BUY
          ? t('page:biddingSpot.side.buy')
          : t('page:biddingSpot.side.sell'),
    },
    {
      key: 'price',
      header: t('page:biddingIntraday.form.priceYenPerKwh'),
      format: 'number',
      cell: (f) =>
        isIntradayMarketOrder(f.bid)
          ? t('page:biddingIntraday.bidTable.marketOrderPrice')
          : f.bid.priceYenPerKwh,
    },
    {
      key: 'volume',
      header: t('page:biddingIntraday.form.volumeMwh'),
      format: 'number',
      cell: (f) => f.bid.volumeMw,
    },
    {
      key: 'reason',
      header: t('page:biddingIntraday.submit.failure.columnReason'),
      cell: (f) => intradaySubmitFailureReason(f, t),
    },
  ]

  return (
    <>
      <Alert
        flexShrink={0}
        description={
          <>
            {conflictCount > 0 && (
              <Text>
                {t('page:biddingIntraday.submit.failure.conflict', {
                  count: conflictCount,
                })}
              </Text>
            )}
            {otherCount > 0 && (
              <Text>
                {t('page:biddingIntraday.submit.failure.other', {
                  count: otherCount,
                })}
              </Text>
            )}
            <Link marginTop={1} asChild>
              <button onClick={() => setDetailOpen(true)} type="button">
                {t('page:biddingIntraday.submit.failure.showDetails')}
              </button>
            </Link>
          </>
        }
        status="error"
        title={
          passedCount > 0
            ? t('page:biddingIntraday.submit.failure.title', {
                count: passedCount,
              })
            : undefined
        }
      >
        <AlertDismissButton onClick={onDismiss} />
      </Alert>
      <Modal
        footer={
          <HStack justifyContent="flex-end">
            <TertiaryButton onClick={() => setDetailOpen(false)}>
              {t('common:action.close')}
            </TertiaryButton>
          </HStack>
        }
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        size="xl"
        title={t('page:biddingIntraday.submit.failure.dialogTitle')}
      >
        <MasterDataTable
          columns={columns}
          getRowKey={(f) => f.bid.id}
          rows={[...failures]}
        />
      </Modal>
    </>
  )
}
